import { TRPCClientError } from '@trpc/client'
import {
  addDays,
  differenceInCalendarWeeks,
  format,
  parseISO,
  startOfWeek,
} from 'date-fns'
import type * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { trpc } from '@/lib/trpc'
import { currentWeekStart } from '../data/homework-data'
import { planToHomework } from '../data/plan-adapter'
import type { HomeworkTask, Sprint } from '../data/types'

interface HomeworkContextValue {
  tasks: HomeworkTask[]
  sprints: Sprint[]
  isLoading: boolean
  error: Error | null
  isCompleted: (task: HomeworkTask) => boolean
  toggleCompleted: (task: HomeworkTask) => void
  isCommentsRead: (task: HomeworkTask) => boolean
  markCommentsRead: (task: HomeworkTask) => void
  isLocked: (task: HomeworkTask) => boolean
  isWeekLocked: (weekStart: Date) => boolean
  showCompleted: boolean
  setShowCompleted: (show: boolean) => void
  moveTask: (taskId: string, dateKey: string) => void
  /** Recenter the queried plan window on the week containing this date */
  setFocusDate: (date: Date) => void
  activeTask: HomeworkTask | null
  openTask: (task: HomeworkTask) => void
  closeTask: () => void
}

const HomeworkContext = createContext<HomeworkContextValue | null>(null)

export function useHomework() {
  const ctx = useContext(HomeworkContext)
  if (!ctx) throw new Error('useHomework must be used within HomeworkProvider')
  return ctx
}

// The list endpoint caps a range at 92 days, so page the plan as a sliding
// window centered near the focused week: 7 weeks of history for the missed-work
// backlog, 6 weeks ahead so a full month grid always fits (91 days total).
const RANGE_WEEKS_BACK = 7
const RANGE_WEEKS_AHEAD = 6

function planRangeFor(focusDate: Date): {
  rangeStart: string
  rangeEndExclusive: string
} {
  const anchored = startOfWeek(focusDate, { weekStartsOn: 1 })
  return {
    rangeStart: format(addDays(anchored, -RANGE_WEEKS_BACK * 7), 'yyyy-MM-dd'),
    rangeEndExclusive: format(
      addDays(anchored, RANGE_WEEKS_AHEAD * 7),
      'yyyy-MM-dd',
    ),
  }
}

/**
 * A failure before the server processed the command (network drop, aborted
 * request). Server-produced errors always carry a data shape.
 */
function isTransportError(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data == null
}

function toError(error: unknown): Error | null {
  if (!error) return null
  if (error instanceof Error) return error
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return new Error(error.message)
  }
  return null
}

export function HomeworkProvider({ children }: { children: React.ReactNode }) {
  const utils = trpc.useUtils()
  const [focusDate, setFocusDateState] = useState(() => new Date())
  const range = useMemo(() => planRangeFor(focusDate), [focusDate])
  const query = trpc.dashboard.plan.list.useQuery(range)
  const completeMutation = trpc.dashboard.plan.completeManual.useMutation()

  const { tasks: baseTasks, sprints } = useMemo(
    () => planToHomework(query.data),
    [query.data],
  )

  const setFocusDate = useCallback((date: Date) => {
    setFocusDateState((prev) =>
      startOfWeek(prev, { weekStartsOn: 1 }).getTime() ===
      startOfWeek(date, { weekStartsOn: 1 }).getTime()
        ? prev
        : date,
    )
  }, [])

  // Local-only reschedule preview. Publishing schedule changes stays a coach
  // action; the client can shuffle their own board without persisting it.
  const [dateOverrides, setDateOverrides] = useState<Record<string, string>>({})

  const tasks = useMemo(
    () =>
      baseTasks.map((t) =>
        dateOverrides[t.id] ? { ...t, date: dateOverrides[t.id] } : t,
      ),
    [baseTasks, dateOverrides],
  )

  const moveTask = useCallback((taskId: string, dateKey: string) => {
    setDateOverrides((prev) =>
      prev[taskId] === dateKey ? prev : { ...prev, [taskId]: dateKey },
    )
  }, [])

  // Optimistic completion state, keyed by assignment id. Safe to apply before
  // the server answers: manual transitions are guarded server-side by
  // authorization and the assignment version CAS.
  const [completedOverrides, setCompletedOverrides] = useState<
    Record<string, boolean>
  >({})
  const [readOverrides, setReadOverrides] = useState<Record<string, boolean>>(
    {},
  )
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null)
  // Derive the active task from the live `tasks` array instead of snapshotting
  // it: a stored copy goes stale after any refetch (e.g. completing a task
  // invalidates the plan), leaving the detail dialog rendering old completion
  // state. Storing the id and re-deriving keeps it in sync.
  const activeTask = useMemo(
    () =>
      activeTaskId ? (tasks.find((t) => t.id === activeTaskId) ?? null) : null,
    [activeTaskId, tasks],
  )
  const [showCompleted, setShowCompleted] = useState(false)

  const isCompleted = useCallback(
    (task: HomeworkTask) => completedOverrides[task.id] ?? task.completed,
    [completedOverrides],
  )

  const clearCompletedOverride = useCallback((taskId: string) => {
    setCompletedOverrides((prev) => {
      if (!(taskId in prev)) return prev
      const { [taskId]: _cleared, ...rest } = prev
      return rest
    })
  }, [])

  const toggleCompleted = useCallback(
    (task: HomeworkTask) => {
      // Submission-review assignments complete on their own — coding work the
      // moment it qualifies, the other kinds on the coach's finish. There is
      // no client-issued manual command for any of them.
      if (task.completionMode !== 'manual') return
      if (task.assignmentVersion === undefined) return

      const target =
        (completedOverrides[task.id] ?? task.completed)
          ? ('active' as const)
          : ('completed' as const)
      // One UUID per user status command; a transport retry replays the exact
      // same variables so the server sees a single idempotent command.
      const command = {
        assignmentId: task.id,
        assignmentVersion: task.assignmentVersion,
        commandId: crypto.randomUUID(),
        target,
      }

      setCompletedOverrides((prev) => ({
        ...prev,
        [task.id]: target === 'completed',
      }))

      void (async () => {
        try {
          try {
            await completeMutation.mutateAsync(command)
          } catch (error) {
            if (!isTransportError(error)) throw error
            await completeMutation.mutateAsync(command)
          }
          await utils.dashboard.plan.list.invalidate()
          clearCompletedOverride(task.id)
        } catch {
          clearCompletedOverride(task.id)
        }
      })()
    },
    [clearCompletedOverride, completeMutation, completedOverrides, utils],
  )

  const isCommentsRead = useCallback(
    (task: HomeworkTask) => readOverrides[task.id] ?? task.commentsRead,
    [readOverrides],
  )

  const markCommentsRead = useCallback((task: HomeworkTask) => {
    if (task.comments.length === 0) return
    setReadOverrides((prev) =>
      prev[task.id] ? prev : { ...prev, [task.id]: true },
    )
  }, [])

  const isWeekLocked = useCallback(
    (weekStart: Date) =>
      differenceInCalendarWeeks(weekStart, currentWeekStart(), {
        weekStartsOn: 1,
      }) > 0,
    [],
  )

  const isLocked = useCallback(
    (task: HomeworkTask) =>
      isWeekLocked(startOfWeek(parseISO(task.date), { weekStartsOn: 1 })),
    [isWeekLocked],
  )

  const openTask = useCallback(
    (task: HomeworkTask) => {
      if (isLocked(task)) return
      setActiveTaskId(task.id)
      markCommentsRead(task)
    },
    [isLocked, markCommentsRead],
  )

  const closeTask = useCallback(() => setActiveTaskId(null), [])

  const value = useMemo(
    () => ({
      tasks,
      sprints,
      isLoading: query.isLoading,
      error: toError(query.error),
      isCompleted,
      toggleCompleted,
      isCommentsRead,
      markCommentsRead,
      isLocked,
      isWeekLocked,
      showCompleted,
      setShowCompleted,
      moveTask,
      setFocusDate,
      activeTask,
      openTask,
      closeTask,
    }),
    [
      tasks,
      sprints,
      query.isLoading,
      query.error,
      isCompleted,
      toggleCompleted,
      isCommentsRead,
      markCommentsRead,
      isLocked,
      isWeekLocked,
      showCompleted,
      moveTask,
      setFocusDate,
      activeTask,
      openTask,
      closeTask,
    ],
  )

  return (
    <HomeworkContext.Provider value={value}>
      {children}
    </HomeworkContext.Provider>
  )
}
